// Utility to convert VAPID base64url public key to Uint8Array (robust)
function urlBase64ToUint8Array(base64String) {
  if (!base64String || typeof base64String !== "string") {
    throw new Error("Invalid VAPID public key: empty or not a string");
  }
  const cleaned = base64String.trim().replace(/\s+/g, "");
  const base64 = cleaned.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const padded = base64 + padding;
  const rawData = window.atob(padded);
  const decoded = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    decoded[i] = rawData.charCodeAt(i);
  }
  // If the key is 64 bytes (missing uncompressed point prefix 0x04), prefix it
  if (decoded.byteLength === 64) {
    const withPrefix = new Uint8Array(65);
    withPrefix[0] = 0x04;
    withPrefix.set(decoded, 1);
    return withPrefix;
  }
  // If already 65 bytes, return as-is
  if (decoded.byteLength === 65) {
    return decoded;
  }
  throw new Error(
    `Invalid VAPID public key format (expected 64 or 65 bytes, got ${decoded.byteLength})`
  );
}

export async function registerServiceWorker() {
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.register("/sw.js");
    return registration;
  }
  return null;
}

export async function enableNotifications(currentSession) {
  const BASE_URL = process.env.REACT_APP_BASE_URL || "";
  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    throw new Error("Push not supported");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notifications permission denied");
  }

  const registration = await registerServiceWorker();
  if (!registration) throw new Error("Service Worker registration failed");
  if (!registration.pushManager) {
    throw new Error(
      "Push unsupported on this device. On iOS, open from Home Screen (iOS 16.4+)."
    );
  }

  // Get VAPID public key
  const keyResp = await fetch(`${BASE_URL}/nocApi/push/public-key`, {
    credentials: "include",
  });

  // Check if response is HTML (error page) instead of JSON
  const contentType = keyResp.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    throw new Error(
      "Server configuration error: Push notifications not properly configured"
    );
  }

  const keyData = await keyResp.json();
  const { publicKey } = keyData;
  if (!publicKey)
    throw new Error("Server configuration error: Missing VAPID key");

  // Subscribe
  const applicationServerKey = urlBase64ToUint8Array(publicKey);
  const sub = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });

  // Send to backend
  const res = await fetch(`${BASE_URL}/nocApi/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ subscription: sub.toJSON(), currentSession }),
  });
  const data = await res.json();
  if (!data.success)
    throw new Error(data.message || "Failed to save subscription");
  return true;
}

export async function disableNotifications(currentSession) {
  const BASE_URL = process.env.REACT_APP_BASE_URL || "";
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return true;
  const sub = await registration.pushManager.getSubscription();
  if (sub) {
    await fetch(`${BASE_URL}/nocApi/push/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        endpoint: sub.endpoint,
        subscription: sub.toJSON(),
        currentSession,
      }),
    });
    await sub.unsubscribe();
  }
  return true;
}
